import { withWindow } from '@shared/utils/withWindow';
import { GUEST_LANGUAGE_COOKIE_NAME } from './i18n.constants';
import { resolveSupportedLocale } from './utils';

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

/**
 * Reads the guest's persisted language pick. Path-scoped to the event handle,
 * so on non-guest routes (e.g. admin) the browser simply doesn't expose it.
 */
export const readGuestLanguageCookie = (): string | undefined =>
  withWindow(global => {
    const entry = global.document.cookie.split('; ').find(cookie => cookie.startsWith(`${GUEST_LANGUAGE_COOKIE_NAME}=`));
    const value = entry?.slice(GUEST_LANGUAGE_COOKIE_NAME.length + 1);
    return value ? decodeURIComponent(value) : undefined;
  }, undefined);

export const writeGuestLanguageCookie = (languageCode: string, eventHandle: string): void => {
  withWindow(global => {
    const secure = global.location.protocol === 'https:' ? '; Secure' : '';
    global.document.cookie = `${GUEST_LANGUAGE_COOKIE_NAME}=${encodeURIComponent(languageCode)}; Path=/${eventHandle}; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
  });
};

export const removeGuestLanguageCookie = (eventHandle: string): void => {
  withWindow(global => {
    global.document.cookie = `${GUEST_LANGUAGE_COOKIE_NAME}=; Path=/${eventHandle}; Max-Age=0; SameSite=Lax`;
  });
};

/**
 * The languages a guest may choose for this event: the couple's picks plus the
 * event language (implicitly always available). Every code is resolved to its
 * supported translation sibling — legacy event locales can be aliases (es-US)
 * or long-tail codes (nl-NL) that must not surface as picker values.
 */
export const getEnabledGuestLanguages = (eventLocale: string | null | undefined, guestDisplayLocales: ReadonlyArray<string> | null | undefined): ReadonlyArray<string> => {
  const resolved = [eventLocale, ...(guestDisplayLocales ?? [])].filter((code): code is string => Boolean(code)).map(code => resolveSupportedLocale(code).locale);
  return [...new Set(resolved)];
};
